import { useCallback, useState } from 'react';

import ApiClient from '../config/ApiClient';
import {
  clearPendingCheckout,
  finishNativeIapPurchase,
  isIosNativeApp,
  savePendingCheckout,
  startNativeIapPurchase,
} from '../utils/nativeCheckoutBridge';
import { dispatchCheckoutSynced } from '../subscriptions/checkoutSyncEvents';
import { parseCheckoutError, type CheckoutErrorDisplay } from '../subscriptions/checkoutErrors';
import type { EntitlementsMap, ProductId } from '../subscriptions/types';
import { normalizeEntitlementsMap } from '../subscriptions/entitlements';

export interface ConfirmCheckoutResult {
  entitlements: EntitlementsMap;
  productId: ProductId;
  assignmentId: number;
  productName: string;
  alreadyFulfilled: boolean;
}

interface CheckoutCreateResponse {
  checkout_url: string;
  session_id: string;
  /** Present when an in-progress trial was upgraded in place (no Stripe Checkout). */
  checkout_kind?: 'trial_upgraded';
}

interface IapPrepareResponse {
  app_account_token: string;
  apple_product_id: string;
}

interface CheckoutConfirmResponse {
  entitlements: EntitlementsMap;
  product_id: ProductId;
  assignment_id: number;
  product_name: string;
  already_fulfilled: boolean;
}

export function useSubscriptionCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CheckoutErrorDisplay | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const confirmCheckout = useCallback(
    async (sessionId: string): Promise<ConfirmCheckoutResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await ApiClient.post<CheckoutConfirmResponse>(
          '/api/subscriptions/confirm/',
          { session_id: sessionId },
        );
        if (response.error || !response.data) {
          setError(parseCheckoutError(response));
          return null;
        }
        return {
          entitlements: normalizeEntitlementsMap(response.data.entitlements),
          productId: response.data.product_id,
          assignmentId: response.data.assignment_id,
          productName: response.data.product_name,
          alreadyFulfilled: response.data.already_fulfilled,
        };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const confirmIapCheckout = useCallback(
    async (params: {
      signedTransaction: string;
      appAccountToken?: string;
      purchaseId?: string;
    }): Promise<ConfirmCheckoutResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await ApiClient.post<CheckoutConfirmResponse>(
          '/api/subscriptions/iap/confirm/',
          {
            signed_transaction: params.signedTransaction,
            ...(params.appAccountToken ? { app_account_token: params.appAccountToken } : {}),
          },
        );
        if (response.error || !response.data) {
          setError(parseCheckoutError(response));
          return null;
        }
        if (params.purchaseId) {
          await finishNativeIapPurchase(params.purchaseId);
        }
        return {
          entitlements: normalizeEntitlementsMap(response.data.entitlements),
          productId: response.data.product_id,
          assignmentId: response.data.assignment_id,
          productName: response.data.product_name,
          alreadyFulfilled: response.data.already_fulfilled,
        };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const startCheckout = useCallback(
    async (params: {
      assignmentId?: number;
      productId: ProductId;
      returnTo?: string;
      skipTrial?: boolean;
    }): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        if (isIosNativeApp()) {
          const prepareBody: Record<string, string | number> = {
            product_id: params.productId,
          };
          if (params.assignmentId !== undefined) {
            prepareBody.assignment_id = params.assignmentId;
          }

          const prepareResponse = await ApiClient.post<IapPrepareResponse>(
            '/api/subscriptions/iap/prepare/',
            prepareBody,
          );
          if (
            prepareResponse.error ||
            !prepareResponse.data?.app_account_token ||
            !prepareResponse.data?.apple_product_id
          ) {
            setError(parseCheckoutError(prepareResponse));
            return false;
          }

          savePendingCheckout({
            productId: params.productId,
            checkoutMethod: 'iap',
            appAccountToken: prepareResponse.data.app_account_token,
            ...(params.assignmentId !== undefined ? { assignmentId: params.assignmentId } : {}),
          });

          const purchaseResult = await startNativeIapPurchase({
            appleProductId: prepareResponse.data.apple_product_id,
            appAccountToken: prepareResponse.data.app_account_token,
          });

          if (purchaseResult.status === 'cancelled') {
            return false;
          }
          if (purchaseResult.status !== 'purchased' || !purchaseResult.signedTransaction) {
            if (purchaseResult.signedTransaction) {
              savePendingCheckout({
                productId: params.productId,
                checkoutMethod: 'iap',
                appAccountToken: prepareResponse.data.app_account_token,
                signedTransaction: purchaseResult.signedTransaction,
                ...(purchaseResult.purchaseId ? { iapPurchaseId: purchaseResult.purchaseId } : {}),
                ...(params.assignmentId !== undefined ? { assignmentId: params.assignmentId } : {}),
              });
            }
            if (purchaseResult.status === 'pending') {
              return true;
            }
            setError({
              message:
                purchaseResult.message ?? 'Purchase could not be completed. Please try again.',
              code: 'unknown',
              retryable: true,
              instruction: 'Use Try again to restart checkout.',
            });
            return false;
          }

          savePendingCheckout({
            productId: params.productId,
            checkoutMethod: 'iap',
            appAccountToken: prepareResponse.data.app_account_token,
            signedTransaction: purchaseResult.signedTransaction,
            ...(purchaseResult.purchaseId ? { iapPurchaseId: purchaseResult.purchaseId } : {}),
            ...(params.assignmentId !== undefined ? { assignmentId: params.assignmentId } : {}),
          });

          const confirmResult = await confirmIapCheckout({
            signedTransaction: purchaseResult.signedTransaction,
            appAccountToken: prepareResponse.data.app_account_token,
            ...(purchaseResult.purchaseId ? { purchaseId: purchaseResult.purchaseId } : {}),
          });
          if (confirmResult) {
            clearPendingCheckout();
            dispatchCheckoutSynced(confirmResult);
          }
          return confirmResult !== null;
        }

        const body: Record<string, string | number | boolean> = {
          product_id: params.productId,
        };
        if (params.assignmentId !== undefined) {
          body.assignment_id = params.assignmentId;
        }
        if (params.returnTo) {
          body.return_to = params.returnTo;
        }
        if (params.skipTrial) {
          body.skip_trial = true;
        }

        const response = await ApiClient.post<CheckoutCreateResponse>(
          '/api/subscriptions/checkout/',
          body,
        );
        if (response.error || !response.data?.checkout_url || !response.data?.session_id) {
          setError(parseCheckoutError(response));
          return false;
        }

        window.location.assign(response.data.checkout_url);
        return true;
      } finally {
        setLoading(false);
      }
    },
    [confirmIapCheckout],
  );

  const restoreIapPurchases = useCallback(async (): Promise<ConfirmCheckoutResult[]> => {
    setLoading(true);
    setError(null);
    try {
      const { restoreNativePurchases } = await import('../utils/nativeCheckoutBridge');
      const restored = await restoreNativePurchases();
      if (!restored.length) {
        return [];
      }

      const response = await ApiClient.post<{ restored: CheckoutConfirmResponse[] }>(
        '/api/subscriptions/iap/restore/',
        {
          signed_transactions: restored
            .map((item) => item.signedTransaction)
            .filter((value): value is string => Boolean(value)),
        },
      );
      if (response.error || !response.data?.restored) {
        setError(parseCheckoutError(response));
        return [];
      }

      const results = response.data.restored.map((item) => ({
        entitlements: normalizeEntitlementsMap(item.entitlements),
        productId: item.product_id,
        assignmentId: item.assignment_id,
        productName: item.product_name,
        alreadyFulfilled: item.already_fulfilled,
      }));

      for (const item of restored) {
        if (item.purchaseId) {
          await finishNativeIapPurchase(item.purchaseId);
        }
      }

      return results;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Stripe Customer Portal URL for the payer, or null when unavailable. */
  const openBillingPortal = useCallback(
    async (assignmentId?: number): Promise<string | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await ApiClient.post<{ portal_url: string }>(
          '/api/subscriptions/billing-portal/',
          assignmentId !== undefined ? { assignment_id: assignmentId } : {},
        );
        if (response.error || !response.data?.portal_url) {
          setError(parseCheckoutError(response));
          return null;
        }
        return response.data.portal_url;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    clearError,
    startCheckout,
    confirmCheckout,
    confirmIapCheckout,
    restoreIapPurchases,
    openBillingPortal,
  };
}
